/**
 * MailChimp Campaigns Component
 * Manages MailChimp campaigns with proper API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { mailchimpService } from '../../services/mailchimpService';
import { useRBAC } from '../../hooks/useRBAC';
import ProfessionalLoader from "../common/ProfessionalLoader";
import MailChimpCampaignDetails from './MailChimpCampaignDetails';
import './MailChimpCampaigns.css';

const MailChimpCampaigns = () => {
  const { showSuccess, showError } = useNotifications();
  const { canManageIntegrations } = useRBAC();
  
  // State management
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  // Table sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const defaultParams = {
        count: 10,
        status: '',
        page: 1
      };
      const response = await mailchimpService.getCampaigns(defaultParams);
      
      if (response.success) {
        setCampaigns(response.data || []);
      } else {
        showError(response.message || 'Failed to load campaigns', 'Error');
      }
    } catch (error) {
      showError('Failed to load campaigns', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  // Load campaigns on component mount
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Handle campaign selection
  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetails(true);
  };

  // Handle send campaign
  const handleSendCampaign = async (campaignId) => {
    setIsProcessing(true);
    try {
      const response = await mailchimpService.sendCampaign(campaignId);
      
      if (response.success) {
        showSuccess('Campaign sent successfully', 'Success');
        loadCampaigns();
      } else {
        showError(response.message || 'Failed to send campaign', 'Error');
      }
    } catch (error) {
      showError('Failed to send campaign', 'Error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle table sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort campaigns based on sortConfig
  const sortedCampaigns = React.useMemo(() => {
    if (!sortConfig.key) return campaigns;
    
    return [...campaigns].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'title') {
        aValue = a.settings?.title || '';
        bValue = b.settings?.title || '';
      } else if (sortConfig.key === 'subject_line') {
        aValue = a.settings?.subject_line || '';
        bValue = b.settings?.subject_line || '';
      } else if (sortConfig.key === 'open_rate') {
        aValue = a.report_summary?.open_rate || 0;
        bValue = b.report_summary?.open_rate || 0;
      } else if (sortConfig.key === 'click_rate') {
        aValue = a.report_summary?.click_rate || 0;
        bValue = b.report_summary?.click_rate || 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [campaigns, sortConfig]);

  if (isLoading) {
    return (
      <div className="mailchimp-campaigns">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="mailchimp-campaigns">
      {/* Header */}
      <div className="campaigns-header">
        <div className="header-content">
          <h2>📧 MailChimp Campaigns</h2>
          <p>Manage and monitor MailChimp email campaigns</p>
        </div>
      </div>


      {/* Campaigns List */}
      <div className="campaigns-content">
        <div className="campaigns-table-container">
          <div className="campaigns-table">
            <table>
            <thead>
              <tr>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('title')}
                  style={{ cursor: 'pointer' }}
                >
                  Campaign Title
                  {sortConfig.key === 'title' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('subject_line')}
                  style={{ cursor: 'pointer' }}
                >
                  Subject Line
                  {sortConfig.key === 'subject_line' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer' }}
                >
                  Status
                  {sortConfig.key === 'status' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('type')}
                  style={{ cursor: 'pointer' }}
                >
                  Type
                  {sortConfig.key === 'type' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('emails_sent')}
                  style={{ cursor: 'pointer' }}
                >
                  Emails Sent
                  {sortConfig.key === 'emails_sent' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('send_time')}
                  style={{ cursor: 'pointer' }}
                >
                  Send Time
                  {sortConfig.key === 'send_time' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('open_rate')}
                  style={{ cursor: 'pointer' }}
                >
                  Open Rate
                  {sortConfig.key === 'open_rate' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('click_rate')}
                  style={{ cursor: 'pointer' }}
                >
                  Click Rate
                  {sortConfig.key === 'click_rate' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.map(campaign => (
                <tr key={campaign.id}>
                  <td>
                    <div className="campaign-title">
                      <span className="title-text">{campaign.settings?.title || 'Untitled Campaign'}</span>
                      <span className="campaign-id">ID: {campaign.id}</span>
                    </div>
                  </td>
                  <td>
                    <div className="campaign-subject">
                      <span className="subject-line">{campaign.settings?.subject_line || 'No Subject'}</span>
                      <span className="from-name">From: {campaign.settings?.from_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${campaign.status}`}>
                      {campaign.status}
                    </span>
                  </td>
                  <td>
                    <span className="campaign-type">
                      {campaign.type || 'regular'}
                    </span>
                  </td>
                  <td>
                    <span className="emails-count">
                      {campaign.emails_sent || 0}
                    </span>
                  </td>
                  <td>
                    <div className="send-time">
                      {campaign.send_time ? 
                        <span className="time-sent">
                          {new Date(campaign.send_time).toLocaleDateString()}
                          <br />
                          <small>{new Date(campaign.send_time).toLocaleTimeString()}</small>
                        </span> : 
                        <span className="not-sent">Not sent</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div className="open-rate">
                      {campaign.report_summary?.open_rate ? 
                        <span className="rate-value">
                          {(campaign.report_summary.open_rate * 100).toFixed(1)}%
                          <br />
                          <small>({campaign.report_summary.unique_opens || 0} opens)</small>
                        </span> : 
                        <span className="no-data">N/A</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div className="click-rate">
                      {campaign.report_summary?.click_rate ? 
                        <span className="rate-value">
                          {(campaign.report_summary.click_rate * 100).toFixed(1)}%
                          <br />
                          <small>({campaign.report_summary.subscriber_clicks || 0} clicks)</small>
                        </span> : 
                        <span className="no-data">N/A</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div className="campaign-actions">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => handleCampaignSelect(campaign)}
                        title="View campaign details"
                      >
                        View
                      </button>
                      {campaign.status === 'save' && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSendCampaign(campaign.id)}
                          disabled={isProcessing || !canManageIntegrations}
                          title="Send campaign"
                        >
                          Send
                        </button>
                      )}
                      {campaign.archive_url && (
                        <a
                          href={campaign.archive_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline"
                          title="View archived campaign"
                        >
                          Archive
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {campaigns.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📧</div>
          <h3>No Campaigns</h3>
          <p>No MailChimp campaigns found for the selected criteria.</p>
        </div>
      )}

      {/* Campaign Details Modal */}
      {showDetails && selectedCampaign && (
        <MailChimpCampaignDetails
          campaign={selectedCampaign}
          onClose={() => {
            setShowDetails(false);
            setSelectedCampaign(null);
          }}
          onSend={() => handleSendCampaign(selectedCampaign.id)}
          canManage={canManageIntegrations}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
};

export default MailChimpCampaigns;
